import { useState } from 'react'
import { NavLink, Link, useNavigate } from 'react-router-dom'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { usePlatformSettings } from '../context/PlatformSettingsContext'
import { AnnouncementsBell } from './AnnouncementsBell'

const navLinks = [
  { id: 'home',  label: 'Home', icon: 'home', path: '/' },
  { id: 'leaderboard', label: 'Leaderboard', icon: 'leaderboard',     path: '/leaderboard', flagKey: 'leaderboard' },
  { id: 'offerwall', label: 'Offerwall', icon: 'redeem', path: '/offerwall', flagKey: 'offerwall' },
  { id: 'staking', label: 'Staking', icon: 'account_balance', path: '/staking', flagKey: 'staking' },
  { id: 'marketplace', label: 'Ads', icon: 'storefront', path: '/marketplace' },
  { id: 'profile', label: 'Profile', icon: 'person', path: '/profile' },
]

const footerLinks = [
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms',   to: '/terms' },
  { label: 'Youth Policy', to: '/youth-protection' },
]

const CIS_TIERS = [
  { key: 'bronze', label: 'Bronze', min: 0, icon: 'workspace_premium', color: 'text-amber-700', barColor: 'bg-amber-700' },
  { key: 'silver', label: 'Silver', min: 40_000, icon: 'military_tech', color: 'text-on-surface-variant', barColor: 'bg-on-surface-variant' },
  { key: 'gold', label: 'Gold', min: 400_000, icon: 'military_tech', color: 'text-primary',  barColor: 'bg-primary-container' },
  { key: 'diamond', label: 'Diamond', min: 4_000_000, icon: 'diamond', barColor: 'bg-blue-400', color: 'text-blue-400' },
]

function getCisTier(score) {
  const safeScore = Number.isFinite(score) ? score : 0
  return [...CIS_TIERS].reverse().find(t => safeScore >= t.min) ?? CIS_TIERS[0]
}

function getNextTier(tier) {
  const i = CIS_TIERS.findIndex(t => t.key === tier.key)
  return CIS_TIERS[i + 1] ?? null
}

export function Icon({ name, className = '', filled = false }) {
  const fill = filled ? "'FILL' 1" : "'FILL' 0"
  return (
    <span
      className={`material-symbols-outlined notranslate ${className}`}
      translate="no"
      style={{ fontVariationSettings: `${fill}, 'wght' 400, 'GRAD' 0, 'opsz' 24` }}
    >
      {name}
    </span>
  )
}

export function TopBar() {
  const { dark, toggle } = useTheme()
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <header className="w-full top-0 fixed z-50 flex justify-between items-center px-4 md:px-20 py-0 bg-background border-b-[4px] border-on-background h-[72px]">

      {/* ── Brand ── */}
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 bg-primary-container border-[3px] border-on-surface overflow-hidden flex items-center justify-center flex-shrink-0"
          style={{ boxShadow: '3px 3px 0px 0px #000' }}
        >
          <img src="/favicon.jpg" alt="Chicago logo" className="w-full h-full object-cover" />
        </div>
        <span
          className="font-extrabold text-[22px] md:text-[26px] uppercase tracking-tight leading-none text-on-surface"
          style={{ letterSpacing: '-0.02em' }}
        >
          Chicago
        </span>
      </div>

      {/* ── Actions ── */}
      <div className="flex items-center gap-2 md:gap-3">

        {/* Theme toggle — visible on all screen sizes */}
        <button
          title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={toggle}
          className="flex items-center justify-center w-10 h-10 border-[3px] border-on-surface bg-surface-container text-on-surface-variant hover:bg-primary-container/10 hover:text-primary-container transition-all"
          style={{ boxShadow: '2px 2px 0px 0px var(--neo-shadow-color)' }}
        >
          <Icon name={dark ? 'light_mode' : 'dark_mode'} className="text-[20px]" />
        </button>

        {/* Announcements bell — visible on all screen sizes */}
        <AnnouncementsBell />

        {/* Connect Wallet — always visible */}
        <ConnectButton.Custom>
          {({ account, chain, openConnectModal, openAccountModal, mounted }) => {
            if (!mounted) return null
            const connected = !!account
            return (
              <button
                type="button"
                onClick={connected ? openAccountModal ?? undefined : openConnectModal ?? undefined}
                className="flex items-center gap-2 px-3 md:px-4 py-2 bg-surface text-on-surface font-bold border-[3px] border-on-surface hover:bg-primary-container/10 transition-all text-sm"
                style={{ boxShadow: '3px 3px 0px 0px var(--neo-shadow-color)' }}
              >
                <Icon name="account_balance_wallet" className="text-[18px]" />
                <span className="hidden sm:inline font-bold text-[11px] uppercase tracking-wider">
                  {connected ? 'Connected' : 'Connect Wallet'}
                </span>
              </button>
            )
          }}
        </ConnectButton.Custom>

        {/* User avatar + logout — desktop only */}
        {user && (
          <div className="hidden lg:flex items-center gap-2">
            {user.avatar ? (
              <img
                src={user.avatar}
                alt={user.name}
                className="w-10 h-10 object-cover border-[3px] border-on-surface cursor-pointer hover:border-primary-container transition-colors"
                style={{ boxShadow: '2px 2px 0px 0px var(--neo-shadow-color)' }}
                onClick={() => navigate('/profile')}
                title={user.name}
              />
            ) : (
              <button
                onClick={() => navigate('/profile')}
                className="w-10 h-10 bg-surface-container border-[3px] border-on-surface flex items-center justify-center hover:bg-primary-container/10 transition-all"
                style={{ boxShadow: '2px 2px 0px 0px var(--neo-shadow-color)' }}
              >
                <Icon name="person" className="text-[20px]" />
              </button>
            )}
            <button
              onClick={handleLogout}
              title="Sign out"
              className="flex items-center justify-center w-10 h-10 border-[3px] border-on-surface bg-surface-container text-on-surface-variant hover:bg-error-container hover:text-on-error-container transition-all"
              style={{ boxShadow: '2px 2px 0px 0px var(--neo-shadow-color)' }}
            >
              <Icon name="logout" className="text-[18px]" />
            </button>
          </div>
        )}
      </div>
    </header>
  )
}

export function LeftSidebar() {
  const { user } = useAuth()
  const { isFeatureEnabled } = usePlatformSettings()

  return (
    <aside className="fixed left-[max(24px,calc(50%-576px))] top-[88px] h-[calc(100vh-100px)] w-[220px] hidden lg:flex flex-col gap-3 overflow-y-auto">

      {/* Nav links */}
      <nav className="flex flex-col border-[4px] border-on-background bg-surface rounded-lg overflow-hidden"
        style={{ boxShadow: '4px 4px 0px 0px var(--neo-shadow-color)' }}
      >
        {navLinks.map((link) => {
          const disabled = link.flagKey && !isFeatureEnabled(link.flagKey)
          return (
            <NavLink
              key={link.id}
              to={link.path}
              end={link.path === '/'}
              className={({ isActive }) =>
                `relative ${disabled ? 'opacity-50' : ''} ${
                  isActive
                    ? 'flex items-center gap-3 px-5 py-3 bg-primary-container text-on-primary-container font-bold transition-all border-b-[3px] border-on-background last:border-b-0'
                    : 'flex items-center gap-3 px-5 py-3 text-on-surface hover:bg-primary-container/10 transition-all border-b-[3px] border-on-background/20 last:border-b-0'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon name={link.icon} className="text-[20px]" filled={isActive} />
                  <span className="font-bold text-[13px] uppercase tracking-wider">{link.label}</span>
                  {disabled && (
                    <span className="ml-auto text-[8px] font-extrabold uppercase tracking-widest bg-on-background/10 px-1.5 py-0.5 rounded">
                      Soon
                    </span>
                  )}
                </>
              )}
            </NavLink>
          )
        })}
      </nav>

      {user && (() => {
        const score = Number(user.cisScore) || 0
        const tier = getCisTier(score)
        const next = getNextTier(tier)
        const tierSpan = next ? next.min - tier.min : 0
        const pct = next && tierSpan > 0
          ? Math.min(100, Math.max(0, Math.round(((score - tier.min) / tierSpan) * 100)))
          : 100

        return (
          <div
            className="border-[4px] border-on-background bg-surface rounded-lg overflow-hidden cursor-default"
            style={{ boxShadow: '4px 4px 0px 0px var(--neo-shadow-color)' }}
          >
            <div className="w-full text-left p-3.5 flex flex-col gap-2.5">
              <div className="flex items-center justify-between">
                <span className="font-bold text-[10px] uppercase tracking-[0.16em] text-on-surface-variant">CIS Score</span>
                <span className={`flex items-center gap-1 font-bold text-[10px] uppercase tracking-widest ${tier.color}`}>
                  <Icon name={tier.icon} className="text-[14px]" filled />
                  {tier.label}
                </span>
              </div>

              <div className="flex items-end gap-2">
                {user.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="w-9 h-9 object-cover border-[3px] border-on-surface flex-shrink-0 rounded"
                  />
                ) : (
                  <div className="w-9 h-9 bg-surface-container border-[3px] border-on-surface flex items-center justify-center flex-shrink-0 rounded">
                    <Icon name="person" className="text-[16px]" />
                  </div>
                )}
                <span className="font-extrabold text-[28px] leading-none text-on-background tabular-nums">
                  {score.toLocaleString()}
                </span>
              </div>

              <div>
                <div className="h-1.5 w-full bg-surface-container-high rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${tier.barColor} transition-all`} style={{ width: `${pct}%` }} />
                </div>
                <p className="text-[10px] text-on-surface-variant mt-1 font-mono">
                  {next ? `${(next.min - score).toLocaleString()} to ${next.label}` : 'Max tier reached'}
                </p>
              </div>
            </div>
          </div>
        )
      })()}
    </aside>
  )
}

export function BottomNav() {
  const { isFeatureEnabled } = usePlatformSettings()

  return (
    <nav className="fixed bottom-0 w-full lg:hidden z-50 flex justify-around items-center h-[64px] px-2 bg-background border-t-[4px] border-on-background"
      style={{ boxShadow: '0px -4px 0px 0px rgba(212,175,55,0.4)' }}
    >
      {navLinks.map((link) => {
        const disabled = link.flagKey && !isFeatureEnabled(link.flagKey)
        return (
          <NavLink
            key={link.id}
            to={link.path}
            end={link.path === '/'}
            className={({ isActive }) =>
              `${disabled ? 'opacity-50' : ''} ${
                isActive
                  ? 'flex flex-col items-center justify-center gap-1 px-3 py-2 bg-primary-container text-on-primary-container border-[2px] border-on-background border-t-[3px] border-t-primary min-w-[56px]'
                  : 'flex flex-col items-center justify-center gap-1 px-3 py-2 text-on-surface-variant hover:text-on-surface min-w-[56px] transition-colors border-[2px] border-transparent'
              }`
            }
          >
            {({ isActive }) => (
              <Icon name={link.icon} className="text-[24px]" filled={isActive} />
            )}
          </NavLink>
        )
      })}
    </nav>
  )
}

function UserRowSkeleton() {
  return (
    <div className="flex items-center justify-between gap-3 animate-pulse">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 bg-on-background/10 flex-shrink-0" />
        <div className="flex flex-col gap-1.5">
          <div className="h-2.5 bg-on-background/10 w-24" />
          <div className="h-2 bg-on-background/10 w-16" />
        </div>
      </div>
      <div className="h-7 w-16 bg-on-background/10 flex-shrink-0" />
    </div>
  )
}

function TrendingRowSkeleton() {
  return (
    <div className="flex flex-col gap-1.5 animate-pulse">
      <div className="h-2 bg-on-background/10 w-20" />
      <div className="h-3 bg-on-background/10 w-36" />
      <div className="h-2 bg-on-background/10 w-12" />
    </div>
  )
}

export function RightSidebar({ suggestedUsers, trendingTopics, onFollow, onHashtagClick }) {
  const [followingMap, setFollowingMap] = useState({})
  const navigate = useNavigate()

  const handleFollow = (userId, currentlyFollowing) => {
    setFollowingMap(prev => ({ ...prev, [userId]: !currentlyFollowing }))
    onFollow?.(userId)
  }

  const isFollowing = (u) =>
    followingMap[u.id] !== undefined ? followingMap[u.id] : u.following

  const loading = !suggestedUsers?.length && !trendingTopics?.length
  return (
    <aside className="fixed right-[max(24px,calc(50%-576px))] top-[88px] w-[260px] hidden lg:flex flex-col gap-4 h-[calc(100vh-100px)] overflow-y-auto no-scrollbar">

      {/* Who to Follow */}
      <section
        className="bg-surface border-[4px] border-on-background p-5"
        style={{ boxShadow: '4px 4px 0px 0px rgba(212,175,55,1)' }}
      >
        <h2 className="font-bold text-[11px] uppercase tracking-[0.14em] mb-5 pb-3 border-b-[3px] border-on-background text-on-surface">
          Who to Follow
        </h2>
        <div className="flex flex-col gap-4">
          {loading ? (
            <>
              <UserRowSkeleton />
              <UserRowSkeleton />
              <UserRowSkeleton />
            </>
          ) : suggestedUsers?.length === 0 ? (
            <p className="text-[11px] text-on-surface-variant/50 font-mono text-center py-2">No suggestions yet</p>
          ) : (
            suggestedUsers.map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-3">
                <button onClick={() => navigate(`/profile/${u.id}`)} className="flex items-center gap-3 min-w-0 hover:opacity-80 transition-opacity text-left">
                  {u.avatar ? (
                    <img
                      src={u.avatar}
                      alt={u.name}
                      className="w-9 h-9 border-[3px] border-on-surface object-cover flex-shrink-0"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }}
                    />
                  ) : null}
                  <div
                    className="w-9 h-9 bg-surface-container border-[3px] border-on-surface items-center justify-center flex-shrink-0 text-on-surface-variant"
                    style={{ display: u.avatar ? 'none' : 'flex' }}
                  >
                    <Icon name="person" className="text-[16px]" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-on-surface text-[13px] truncate">{u.name}</p>
                    <p className="text-[11px] text-on-surface-variant font-mono truncate">{u.handle}</p>
                  </div>
                </button>
                <button
                  onClick={() => handleFollow(u.id, isFollowing(u))}
                  className={`px-3 py-1.5 font-bold border-[2px] border-on-surface text-[11px] uppercase tracking-wider flex-shrink-0 transition-all hover:translate-x-[1px] hover:translate-y-[1px]
                    ${isFollowing(u)
                      ? 'bg-primary-container text-on-primary-container'
                      : 'bg-surface text-on-surface hover:bg-surface-container'
                    }`}
                  style={{ boxShadow: '2px 2px 0px 0px #000' }}
                >
                  {isFollowing(u) ? 'Following' : 'Follow'}
                </button>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Trending */}
      <section
        className="bg-surface border-[4px] border-on-background p-5"
        style={{ boxShadow: '4px 4px 0px 0px var(--neo-shadow-color)' }}
      >
        <h2 className="font-bold text-[11px] uppercase tracking-[0.14em] mb-5 pb-3 border-b-[3px] border-on-background text-on-surface">
          Trending
        </h2>
        <div className="flex flex-col gap-4">
          {loading ? (
            <>
              <TrendingRowSkeleton />
              <TrendingRowSkeleton />
              <TrendingRowSkeleton />
            </>
          ) : trendingTopics?.length === 0 ? (
            <p className="text-[11px] text-on-surface-variant/50 font-mono text-center py-2">Nothing trending yet</p>
          ) : (
            trendingTopics.map((topic) => (
              <button
                key={topic.id}
                onClick={() => onHashtagClick?.(topic.hashtag)}
                className="group block text-left w-full hover:opacity-80 transition-opacity"
              >
                <p className="text-[11px] text-primary font-bold font-mono mb-0.5">{topic.hashtag}</p>
                <p className="font-bold text-on-surface text-[13px] group-hover:underline decoration-2 underline-offset-2">{topic.title}</p>
                <p className="text-[10px] text-on-surface-variant font-mono">{topic.postCount} Posts</p>
              </button>
            ))
          )}
        </div>
      </section>

      <footer className="px-1 flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase font-bold tracking-widest text-on-surface-variant/50">
        {footerLinks.map((l) => (
          <Link key={l.label} to={l.to} className="hover:text-primary transition-colors">{l.label}</Link>
        ))}
        <span>© 2025 Chicago</span>
      </footer>
    </aside>
  )
}